import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { Delete, Lock, Unlock } from "lucide-react";

export default function LoginPage() {
  const [pin, setPin] = useState("");
  const [isSetupMode, setIsSetupMode] = useState(false);
  const [confirmPin, setConfirmPin] = useState("");
  const [step, setStep] = useState(1); // 1 = enter, 2 = confirm (for setup)
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, isSetup, loading: authLoading, login, setupComplete } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Only set setup mode if isSetup is explicitly false (not null/undefined)
    if (isSetup === false) {
      setIsSetupMode(true);
    } else if (isSetup === true) {
      setIsSetupMode(false);
    }
  }, [isSetup]);

  const handleNumberClick = (num) => {
    if (step === 1 && pin.length < 6) {
      setPin((prev) => prev + num);
    } else if (step === 2 && confirmPin.length < 6) {
      setConfirmPin((prev) => prev + num);
    }
  };

  const handleDelete = () => {
    if (step === 1) {
      setPin((prev) => prev.slice(0, -1));
    } else {
      setConfirmPin((prev) => prev.slice(0, -1));
    }
  };

  const handleClear = () => {
    if (step === 1) {
      setPin("");
    } else {
      setConfirmPin("");
    }
  };

  const handleSubmit = useCallback(async () => {
    if (isSetupMode) {
      if (step === 1) {
        if (pin.length < 4) {
          toast.error("PIN must be at least 4 digits");
          return;
        }
        setStep(2);
        return;
      }

      if (pin !== confirmPin) {
        toast.error("PINs do not match");
        setConfirmPin("");
        return;
      }

      setLoading(true);
      try {
        const response = await axios.post(`${API}/auth/setup`, { pin });
        toast.success("PIN set up successfully!");
        setupComplete(response.data.token);
        navigate("/");
      } catch (error) {
        const errorMsg = error.response?.data?.detail || "Setup failed";
        // If PIN already set up, switch to login mode
        if (errorMsg.includes("already set up")) {
          toast.info("PIN already set up. Please enter your PIN to login.");
          setIsSetupMode(false);
          setPin("");
          setStep(1);
        } else {
          toast.error(errorMsg);
        }
      } finally {
        setLoading(false);
      }
    } else {
      if (pin.length < 4) {
        toast.error("Enter your PIN");
        return;
      }

      setLoading(true);
      try {
        const response = await axios.post(`${API}/auth/verify`, { pin });
        toast.success("Welcome back!");
        login(response.data.token);
        navigate("/");
      } catch (error) {
        toast.error(error.response?.data?.detail || "Invalid PIN");
        setPin("");
      } finally {
        setLoading(false);
      }
    }
  }, [isSetupMode, step, pin, confirmPin, setupComplete, login, navigate]);

  // Auto-submit when PIN reaches 6 digits
  useEffect(() => {
    if (pin.length === 6 && !isSetupMode) {
      handleSubmit();
    }
  }, [pin, isSetupMode, handleSubmit]);

  useEffect(() => {
    if (confirmPin.length === 6 && isSetupMode && step === 2) {
      handleSubmit();
    }
  }, [confirmPin, isSetupMode, step, handleSubmit]);

  const currentPin = step === 1 ? pin : confirmPin;

  // Show loading while checking auth status
  if (authLoading) {
    return (
      <div className="min-h-screen login-bg flex items-center justify-center p-4">
        <div className="login-overlay absolute inset-0" />
        <div className="relative z-10 text-center">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4">
      <div className="login-overlay absolute inset-0" />

      <div
        className="relative z-10 w-full max-w-sm animate-fade-in"
        data-testid="login-container"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-indigo-600/20 flex items-center justify-center">
            {currentPin.length > 0 ? (
              <Unlock className="w-8 h-8 text-indigo-400" />
            ) : (
              <Lock className="w-8 h-8 text-indigo-400" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-2">
            Family<span className="text-indigo-500">Sync</span>
          </h1>
          <p className="text-slate-400">
            {isSetupMode
              ? step === 1
                ? "Create your family PIN"
                : "Confirm your PIN"
              : "Enter your family PIN"}
          </p>
        </div>

        {/* PIN Display */}
        <div
          className="flex justify-center gap-3 mb-8"
          data-testid="pin-display"
        >
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full transition-all duration-200 ${
                i < currentPin.length
                  ? "bg-indigo-500 scale-125"
                  : "bg-slate-700"
              }`}
            />
          ))}
        </div>

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-4 max-w-xs mx-auto mb-6">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              key={num}
              onClick={() => handleNumberClick(num.toString())}
              disabled={loading}
              className="w-16 h-16 rounded-full bg-slate-800/50 hover:bg-slate-700/50 text-white text-2xl font-medium transition-all hover:scale-105 active:scale-95 disabled:opacity-50 mx-auto"
              data-testid={`keypad-${num}`}
            >
              {num}
            </button>
          ))}
          <button
            onClick={handleClear}
            disabled={loading}
            className="w-16 h-16 rounded-full bg-slate-800/30 hover:bg-slate-700/30 text-slate-400 text-sm font-medium transition-all mx-auto"
            data-testid="keypad-clear"
          >
            Clear
          </button>
          <button
            onClick={() => handleNumberClick("0")}
            disabled={loading}
            className="w-16 h-16 rounded-full bg-slate-800/50 hover:bg-slate-700/50 text-white text-2xl font-medium transition-all hover:scale-105 active:scale-95 disabled:opacity-50 mx-auto"
            data-testid="keypad-0"
          >
            0
          </button>
          <button
            onClick={handleDelete}
            disabled={loading}
            className="w-16 h-16 rounded-full bg-slate-800/30 hover:bg-slate-700/30 text-slate-400 transition-all flex items-center justify-center mx-auto"
            data-testid="keypad-delete"
          >
            <Delete size={20} />
          </button>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={loading || currentPin.length < 4}
          className="w-full py-4 rounded-xl bg-indigo-600/80 hover:bg-indigo-600 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="submit-button"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {isSetupMode ? "Setting up..." : "Verifying..."}
            </span>
          ) : isSetupMode ? (
            step === 1 ? (
              "Continue"
            ) : (
              "Set PIN"
            )
          ) : (
            "Unlock"
          )}
        </button>

        {/* Back button for setup confirmation */}
        {isSetupMode && step === 2 && (
          <button
            onClick={() => {
              setStep(1);
              setConfirmPin("");
            }}
            className="w-full mt-3 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Back
          </button>
        )}
      </div>
    </div>
  );
}
